import React, { useEffect, useMemo, useRef } from 'react';
import { Chart, ChartData, ChartOptions, ChartType, registerables } from 'chart.js';
import InsightsList, { InsightItem } from './InsightsList';

Chart.register(...registerables);

const DEFAULT_COLORS = [
  { border: '#6366F1', background: 'rgba(99, 102, 241, 0.2)' },
  { border: '#8B5CF6', background: 'rgba(139, 92, 246, 0.2)' },
  { border: '#06B6D4', background: 'rgba(6, 182, 212, 0.2)' },
  { border: '#F59E0B', background: 'rgba(245, 158, 11, 0.2)' },
  { border: '#EC4899', background: 'rgba(236, 72, 153, 0.2)' }
];

export interface ChartCardProps {
  title: string;
  description?: string;
  chartType?: ChartType;
  data: ChartData<ChartType>;
  options?: ChartOptions<ChartType>;
  insights?: ReadonlyArray<InsightItem>;
  height?: number;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
}

const defaultOptions: ChartOptions<ChartType> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        color: 'rgba(255, 255, 255, 0.8)',
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      backgroundColor: 'rgba(15, 23, 42, 0.9)',
      titleColor: '#fff',
      bodyColor: '#fff',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1
    }
  },
  scales: {
    x: {
      grid: { color: 'rgba(255, 255, 255, 0.1)' },
      ticks: {
        color: 'rgba(255, 255, 255, 0.7)',
        font: { size: 11 }
      }
    },
    y: {
      grid: { color: 'rgba(255, 255, 255, 0.1)' },
      ticks: {
        color: 'rgba(255, 255, 255, 0.7)',
        font: { size: 11 }
      }
    }
  }
};

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  description,
  chartType = 'line',
  data,
  options,
  insights,
  height = 280,
  actions,
  footer
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  const mergedData = useMemo(() => {
    const cloned = JSON.parse(JSON.stringify(data)) as ChartData<ChartType>;

    if (cloned.datasets) {
      cloned.datasets = cloned.datasets.map((dataset, index) => {
        const palette = DEFAULT_COLORS[index % DEFAULT_COLORS.length];
        return {
          borderColor: palette.border,
          backgroundColor: chartType === 'line' || chartType === 'radar' ? palette.background : palette.border,
          borderWidth: 2,
          tension: 0.4,
          fill: chartType === 'line',
          ...dataset
        };
      });
    }

    return cloned;
  }, [data, chartType]);

  const mergedOptions = useMemo(() => {
    if (chartType === 'doughnut' || chartType === 'pie') {
      return {
        ...defaultOptions,
        plugins: {
          ...defaultOptions.plugins,
          legend: {
            position: 'bottom',
            labels: {
              color: 'rgba(255, 255, 255, 0.8)',
              font: { size: 12 },
              padding: 20
            }
          }
        },
        scales: undefined,
        ...options
      } as ChartOptions<ChartType>;
    }

    return {
      ...defaultOptions,
      ...options
    };
  }, [options, chartType]);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    chartInstanceRef.current = new Chart(ctx, {
      type: chartType,
      data: mergedData,
      options: mergedOptions
    });

    return () => {
      chartInstanceRef.current?.destroy();
      chartInstanceRef.current = null;
    };
  }, [mergedData, mergedOptions, chartType]);

  return (
    <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {description && <p className="text-white/60 text-sm mt-1">{description}</p>}
        </div>
        {actions}
      </div>
      <div className="relative" style={{ height }}>
        <canvas ref={canvasRef} />
      </div>
      {footer}
      {insights && insights.length > 0 && <InsightsList insights={insights} />}
    </div>
  );
};

export default ChartCard;

